/** 세션에 보존한 사본/삭제행 초안의 복구 진입점. 메뉴를 닫아도 작업 상태는 ws에 남는다. */
import { useEffect, useState } from 'react';
import { Button } from '../common';
import QueryEditor from './QueryEditor';
import { canAddChild, pathNames } from './treeIndex';
import { describeExpr } from './syllabusQuery';
import { makeNames } from './names';
import { describeSyllabusError } from './syllabusApi';
import MathName from './MathName';

const inputCls = 'min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none';
const boxCls = 'space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800';

/** 더보기와 전역 복구 안내가 같은 상태/조작을 사용한다. */
export function CopyRecoveryControls({ recovery, onRetry, onDiscard, onChangeDraft, onOpenCopy, dict = null, labelNames = {} }) {
  const [targetId, setTargetId] = useState(recovery?.targetId || '');
  const [error, setError] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [working, setWorking] = useState(false);
  useEffect(() => { setTargetId(recovery?.targetId || ''); }, [recovery?.copyId, recovery?.targetId]);
  if (!recovery) return null;
  const busy = working || ['creating', 'loading', 'saving'].includes(recovery.status);
  const retry = async () => {
    if (busy || !onRetry) return;
    setWorking(true); setError(null);
    try { await onRetry({ ...(targetId ? { targetId } : {}) }); }
    catch (e) { setError(describeSyllabusError(e)); }
    finally { setWorking(false); }
  };
  return (
    <div className="space-y-2">
      <p role="status" className={boxCls}>
        {recovery.copyId ? `사본 생성됨 · 조건 반영 안 됨${error || recovery.error ? ` — ${error || recovery.error}` : ''}` : '사본 만드는 중'}
        {recovery.sourceName && <span className="block">보관 중인 초안: <MathName text={recovery.sourceName} compact /></span>}
      </p>
      <p className="text-[11px] text-gray-500">초안과 사본을 기억하고 있습니다. 다시 시도해도 사본을 추가로 만들지 않습니다.</p>
      {recovery.candidates?.length > 0 && (
        <label className="block space-y-1 text-xs text-gray-700">
          <span>조건을 반영할 사본 행</span>
          <select data-autofocus aria-label="조건을 반영할 사본 행" className={`${inputCls} w-full`} value={targetId} disabled={busy} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">행을 선택하세요</option>
            {recovery.candidates.map((c) => <option key={c.nodeId} value={c.nodeId}>{c.path} (행 {c.nodeId})</option>)}
          </select>
        </label>
      )}
      {onChangeDraft && recovery.draft && (
        <details className="rounded-lg bg-white p-2 text-xs text-gray-600">
          <summary className="cursor-pointer">보관한 조건 확인·수정</summary>
          <div className="mt-2"><QueryEditor value={recovery.draft} onChange={onChangeDraft} readOnly={busy} dict={dict} labelNames={labelNames} /></div>
        </details>
      )}
      {discarding ? (
        <div className="space-y-2 text-xs text-gray-700">
          <p>사본은 그대로 두고, 보관한 조건의 반영 작업을 종료할까요?</p>
          <div className="flex flex-wrap justify-end gap-1.5">
            <Button size="xs" variant="secondary" onClick={() => setDiscarding(false)}>계속 보관</Button>
            <Button size="xs" variant="danger" onClick={() => onDiscard?.()}>조건 반영 종료</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-1.5">
          {onDiscard && <Button size="xs" variant="ghost" disabled={busy} onClick={() => setDiscarding(true)}>조건 반영 취소</Button>}
          {recovery.copyId && onOpenCopy && <Button size="xs" data-autofocus={!onRetry || undefined} variant="secondary" disabled={busy} onClick={() => onOpenCopy(recovery.copyId)}>사본 열기</Button>}
          {onRetry && <Button size="xs" loading={busy} disabled={!recovery.copyId || (recovery.status === 'choose' && !targetId)} onClick={retry}>조건 반영 다시 시도</Button>}
        </div>
      )}
    </div>
  );
}

function OrphanDraft({ draft, ws, dict, labelNames }) {
  const [name, setName] = useState(draft.name);
  const [parentId, setParentId] = useState(ws.index.byId.has(String(draft.parentId)) ? String(draft.parentId) : '');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const save = async () => {
    if (working) return;
    setWorking(true); setError(null);
    try { await ws.recoverOrphanDraft(draft.nodeId, { name, parentId: parentId || null }); }
    catch (e) { setError(describeSyllabusError(e)); }
    finally { setWorking(false); }
  };
  return (
    <section aria-label={`${draft.name} 초안 복구`} className={boxCls}>
      <p className="font-medium">삭제된 행의 초안: <MathName text={draft.name} compact /></p>
      <p>서버에서 행이 삭제되었습니다. 초안을 새 행으로 보관하거나 명시적으로 폐기할 수 있습니다.</p>
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-0 flex-1 flex-col gap-1">새 행 이름<input aria-label="복구할 새 행 이름" className={inputCls} value={name} maxLength={200} disabled={working} onChange={(e) => setName(e.target.value)} /></label>
        <label className="flex min-w-0 flex-1 flex-col gap-1">보관 위치<select aria-label="초안 보관 위치" className={inputCls} value={parentId} disabled={working} onChange={(e) => setParentId(e.target.value)}>
          <option value="">최상위</option>
          {[...ws.index.byId.keys()].filter((id) => canAddChild(ws.index, id)).map((id) => <option key={id} value={id}>{pathNames(ws.index, id).join(' › ')}</option>)}
        </select></label>
      </div>
      <details className="rounded-lg bg-white p-2 text-gray-600"><summary className="cursor-pointer">보관한 조건 확인·수정</summary><div className="mt-2"><QueryEditor value={draft.query} onChange={(q) => ws.setDraft(draft.nodeId, q)} readOnly={working} dict={dict} labelNames={labelNames} /></div></details>
      {error && <p role="alert" className="text-red-600">{error}</p>}
      {!ws.owner && <p>이 강의에 쓰기 권한이 없어 새 행으로 저장할 수 없습니다. 권한을 확인할 때까지 초안을 보관합니다.</p>}
      <div className="flex flex-wrap justify-end gap-1.5">
        {discarding ? <><span>이 초안을 폐기할까요?</span><Button size="xs" variant="secondary" onClick={() => setDiscarding(false)}>계속 보관</Button><Button size="xs" variant="danger" onClick={() => ws.discardOrphanDraft(draft.nodeId)}>초안 폐기</Button></>
          : <><Button size="xs" variant="ghost" disabled={working} onClick={() => setDiscarding(true)}>폐기…</Button><Button size="xs" loading={working} disabled={!ws.owner || !name.trim()} onClick={save}>새 행으로 보관</Button></>}
      </div>
    </section>
  );
}

export default function DraftRecoveryPanel({ ws, onOpenCopy, dict = null, labelNames = {} }) {
  const conflictId = ws.conflict?.nodeId;
  if (!ws.copyRecovery && !ws.orphanDrafts?.length && !conflictId) return null;
  const names = makeNames(dict, labelNames);
  return (
    <aside aria-label="보관 중인 초안 복구" className="max-h-[45vh] shrink-0 space-y-2 overflow-y-auto rounded-2xl bg-white p-3 shadow-lg">
      {conflictId && <section aria-label="충돌한 조건 비교" className={boxCls}>
        <p className="font-medium">저장 충돌 · 조건 비교</p>
        <dl className="grid gap-2 sm:grid-cols-3">
          <div><dt className="font-medium">편집 시작 때 조건</dt><dd className="break-words"><MathName text={describeExpr(ws.conflict.baseQuery, names) || '조건 없는 제목행'} compact /></dd></div>
          <div><dt className="font-medium">지금 저장된 조건</dt><dd className="break-words"><MathName text={describeExpr(ws.index.byId.get(conflictId)?.query, names) || '조건 없는 제목행'} compact /></dd></div>
          <div><dt className="font-medium">내 초안</dt><dd className="break-words"><MathName text={describeExpr(ws.queryOf(conflictId), names) || '조건 없는 제목행'} compact /></dd></div>
        </dl>
        <div className="flex flex-wrap justify-end gap-2"><Button size="xs" variant="secondary" onClick={() => ws.resolveConflict('theirs')} title="내 초안을 버리고 지금 저장된 조건을 씁니다">지금 저장된 조건 쓰기</Button><Button size="xs" onClick={() => ws.resolveConflict('mine')}>내 변경 다시 저장</Button></div>
      </section>}
      {ws.copyRecovery && <CopyRecoveryControls recovery={ws.copyRecovery} onRetry={ws.retryCopyRecovery} onDiscard={ws.discardCopyRecovery} onChangeDraft={ws.setCopyRecoveryDraft} onOpenCopy={onOpenCopy} dict={dict} labelNames={labelNames} />}
      {(ws.orphanDrafts || []).map((draft) => <OrphanDraft key={draft.nodeId} draft={draft} ws={ws} dict={dict} labelNames={labelNames} />)}
    </aside>
  );
}
